/**
 * Price range filter component.
 *
 * Allows users to set min/max price bounds.
 */

'use client';

import { useSearchParams } from '@/lib/algolia/hooks/useSearchParams';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import styles from './PriceRangeFilter.module.scss';

interface PriceRangeFilterProps {
  minPrice?: number;
  maxPrice?: number;
}

export function PriceRangeFilter({ minPrice, maxPrice }: PriceRangeFilterProps) {
  const { setParams } = useSearchParams();
  const t = useTranslations('plp');
  const [min, setMin] = useState(minPrice?.toString() || '');
  const [max, setMax] = useState(maxPrice?.toString() || '');
  const [isExpanded, setIsExpanded] = useState(!!minPrice || !!maxPrice);

  // Sync local state with URL params when they change externally
  // (e.g., when user clicks "Clear all filters" or uses browser back button)

  useEffect(() => {
    setMin(minPrice?.toString() || '');
    setMax(maxPrice?.toString() || '');
  }, [minPrice, maxPrice]);

  const handleApply = () => {
    const minVal = min ? parseInt(min, 10) : null;
    const maxVal = max ? parseInt(max, 10) : null;

    setParams({
      'price-min': minVal && !isNaN(minVal) ? minVal : null,
      'price-max': maxVal && !isNaN(maxVal) ? maxVal : null,
      page: 1,
    });
  };

  const handleClear = () => {
    setMin('');
    setMax('');
    setParams({
      'price-min': null,
      'price-max': null,
      page: 1,
    });
  };

  const hasValue = minPrice !== undefined || maxPrice !== undefined;

  return (
    <div className={styles.priceFilter}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className={styles.name}>
          {t('facets.price')}
          {hasValue && <span className={styles.active}>({t('activeFilter')})</span>}
        </span>
        <svg
          className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isExpanded && (
        <div className={styles.content}>
          <div className={styles.inputs}>
            <div className={styles.inputGroup}>
              <label htmlFor="price-min" className={styles.label}>
                {t('priceMin')}
              </label>
              <input
                type="number"
                id="price-min"
                value={min}
                onChange={(e) => setMin(e.target.value)}
                placeholder="0"
                min="0"
                className={styles.input}
              />
            </div>
            <span className={styles.separator}>–</span>
            <div className={styles.inputGroup}>
              <label htmlFor="price-max" className={styles.label}>
                {t('priceMax')}
              </label>
              <input
                type="number"
                id="price-max"
                value={max}
                onChange={(e) => setMax(e.target.value)}
                placeholder={t('priceAny')}
                min="0"
                className={styles.input}
              />
            </div>
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              onClick={handleClear}
              className={styles.clearButton}
              disabled={!min && !max}
            >
              {t('clearFilters')}
            </button>
            <button type="button" onClick={handleApply} className={styles.applyButton}>
              {t('applyFilters')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
